import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, LifeBuoy, CheckCircle2, 
  AlertCircle, Loader2, Send, MessageSquare 
} from 'lucide-react';
import axios from 'axios'; 
import { baseurl } from '../../baseurl';

const AddTicket = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    subject: '',
    category: 'General Inquiry',
    description: ''
  });

  const categories = [
    "General Inquiry",
    "Technical Issue",
    "Billing/Payment",
    "Feature Request",
    "Other"
  ];

  const toggleModal = () => {
    if (status === 'processing') return; 
    setIsOpen(!isOpen);
    setFormData({ subject: '', category: 'General Inquiry', description: '' });
    setStatus('idle');
    setMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('processing');
    
    const userToken = localStorage.getItem('userToken');

    try {
      const response = await axios.post(`${baseurl}createTicket`, formData, {
        headers: { Authorization: `Bearer ${userToken}` }
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || "Your ticket has been created successfully!");
      }
    } catch (error) {
      console.error("TICKET API ERROR:", error);
      setStatus('error');
      setMessage(error.response?.data?.message || 'Failed to submit ticket. Please try again.');
    }
  };

  return (
    <>
      <button className="btn-save-changes" onClick={toggleModal}>
        <PlusCircle size={18} /> Raise a Ticket
      </button>

      {isOpen && (
        <div className="st-modal-overlay">
          <div className="st-modal-container">
            {/* Header */}
            <div className="st-modal-header">
              <div className="st-header-info">
                <div className="st-icon-box">
                  <LifeBuoy size={20} color="#4f46e5" />
                </div>
                <div>
                  <h3>New Support Ticket</h3>
                  <p>We usually respond within 24 hours</p>
                </div>
              </div>
              {status !== 'processing' && (
                <button className="st-close-btn" onClick={toggleModal}>&times;</button>
              )}
            </div>

            <div className="st-modal-body">
              {/* IDLE VIEW: THE FORM */}
              {status === 'idle' && (
                <form onSubmit={handleSubmit}>
                  <div className="st-form-group">
                    <label>Category</label>
                    <select 
                      name="category" 
                      className="st-input" 
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>

                  <div className="st-form-group">
                    <label>Subject</label>
                    <input 
                      type="text" 
                      name="subject"
                      className="st-input" 
                      placeholder="Briefly describe the issue"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="st-form-group">
                    <label>Description</label>
                    <textarea 
                      name="description"
                      className="st-input st-textarea" 
                      placeholder="Please provide as much detail as possible..."
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>

                  <div className="st-modal-footer">
                    <button type="button" className="st-btn-secondary" onClick={toggleModal}>Cancel</button>
                    <button type="submit" className="st-btn-primary">
                      <Send size={16} /> Submit Ticket
                    </button>
                  </div>
                </form>
              )}

              {/* PROCESSING VIEW */}
              {status === 'processing' && (
                <div className="st-status-view">
                  <Loader2 className="st-spinner" size={48} />
                  <h4>Submitting Ticket</h4>
                  <p>Please wait while we process your request...</p>
                </div>
              )}

              {/* SUCCESS VIEW */}
              {status === 'success' && (
                <div className="st-status-view">
                  <div className="st-success-icon">
                    <CheckCircle2 size={50} />
                  </div>
                  <h4>Ticket Received!</h4>
                  <p>{message}</p>
                  <button className="st-btn-primary" onClick={toggleModal}>Done</button>
                </div>
              )}

              {/* ERROR VIEW */}
              {status === 'error' && (
                <div className="st-status-view">
                  <AlertCircle size={50} color="#ef4444" />
                  <h4>Submission Failed</h4>
                  <p>{message}</p>
                  <button className="st-btn-primary" onClick={() => setStatus('idle')}>Try Again</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .st-btn-open {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #4f46e5;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: 0.2s;
        }
        .st-btn-open:hover { background: #4338ca; }

        .st-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(15, 23, 42, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
        }

        .st-modal-container {
          background: white;
          width: 95%;
          max-width: 500px;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .st-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .st-header-info { display: flex; gap: 12px; }
        .st-icon-box {
          background: #eef2ff;
          padding: 10px;
          border-radius: 10px;
          display: flex;
          align-items: center;
        }

        .st-header-info h3 { margin: 0; font-size: 1.1rem; color: #1e293b; }
        .st-header-info p { margin: 0; font-size: 0.85rem; color: #64748b; }

        .st-close-btn {
          background: none; border: none; font-size: 24px;
          color: #94a3b8; cursor: pointer;
        }

        .st-modal-body { padding: 24px; }

        .st-form-group { margin-bottom: 18px; }
        .st-form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 6px;
        }

        .st-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .st-input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1); }

        .st-textarea { min-height: 120px; resize: vertical; }

        .st-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }

        .st-btn-secondary {
          background: white; color: #475569;
          border: 1px solid #e2e8f0;
          padding: 10px 18px; border-radius: 8px;
          font-weight: 600; cursor: pointer;
        }

        .st-btn-primary {
          background: #4f46e5; color: white;
          border: none; padding: 10px 18px;
          border-radius: 8px; font-weight: 600;
          cursor: pointer; display: flex; align-items: center; gap: 8px;
        }

        .st-status-view {
          text-align: center;
          padding: 20px 0;
        }

        .st-spinner {
          animation: spin 1s linear infinite;
          color: #4f46e5;
          margin-bottom: 16px;
        }

        .st-success-icon { color: #10b981; margin-bottom: 16px; }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AddTicket;